/**
 * Self-asserted attributes (`DETAILS_SUBMITTED`).
 *
 * IAL 1 is the only assurance level where attributes are self-asserted — every
 * IAL 2.x control checks them against an authoritative source instead. So this
 * form exists to make the IAL 1 baseline reachable, not as a general profile
 * editor.
 *
 * The field list mirrors the server's allowlist exactly
 * (`collection_flow::ATTRIBUTE_FIELDS`). The server REJECTS unknown keys rather
 * than dropping them, so a field added here without being added there fails
 * loudly with `unknown_attribute` — which is the intended direction: the server
 * is the authority and the client must not be able to widen the write surface.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BirthDateInput } from './BirthDateInput';
import { CountrySelect } from './CountrySelect';
import { PhoneInput } from './PhoneInput';

/** Keep in step with `ATTRIBUTE_FIELDS` in `collection_flow.rs`. */
const FIELDS = [
  { name: 'firstName', maxLength: 128, type: 'text', autoComplete: 'given-name' },
  { name: 'lastName', maxLength: 128, type: 'text', autoComplete: 'family-name' },
  // ISO YYYY-MM-DD from ./BirthDateInput (wheels on a phone, a calendar above).
  { name: 'dateOfBirth', maxLength: 32, type: 'date', autoComplete: 'bday' },
  // An ISO alpha-2 code from ./CountrySelect, not free text (see there for why).
  { name: 'nationality', maxLength: 64, type: 'country', autoComplete: 'country' },
  { name: 'phone', maxLength: 32, type: 'tel', autoComplete: 'tel' },
  { name: 'email', maxLength: 254, type: 'email', autoComplete: 'email' },
] as const;

interface DetailsFormProps {
  busy: boolean;
  onSubmit: (values: Record<string, string>) => void;
}

export function DetailsForm({ busy, onSubmit }: DetailsFormProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});

  // Blank fields are omitted rather than sent empty: the server treats an empty
  // string as "not supplied" and refuses a payload with nothing in it, so
  // sending them would turn a partially-filled form into a confusing 400.
  const filled = Object.fromEntries(
    Object.entries(values).filter(([, v]) => v.trim().length > 0),
  );
  const canSubmit = Object.keys(filled).length > 0 && !busy;

  return (
    <form
      className="details-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(filled);
      }}
    >
      {FIELDS.map((field) =>
        field.type === 'country' ? (
          <div key={field.name} className="details-field">
            <span className="details-label" aria-hidden="true">
              {t(`flow.details.${field.name}`, { defaultValue: field.name })}
            </span>
            <CountrySelect
              name={field.name}
              value={values[field.name] ?? ''}
              onChange={(code) => setValues((prev) => ({ ...prev, [field.name]: code }))}
              label={t(`flow.details.${field.name}`, { defaultValue: field.name })}
              placeholder={t(`flow.details.placeholder.${field.name}`, { defaultValue: '' })}
            />
          </div>
        ) : field.type === 'date' ? (
          <div key={field.name} className="details-field">
            <span className="details-label" aria-hidden="true">
              {t(`flow.details.${field.name}`, { defaultValue: field.name })}
            </span>
            <BirthDateInput
              name={field.name}
              value={values[field.name] ?? ''}
              onChange={(iso) => setValues((prev) => ({ ...prev, [field.name]: iso }))}
              label={t(`flow.details.${field.name}`, { defaultValue: field.name })}
              placeholder={t(`flow.details.placeholder.${field.name}`, { defaultValue: '' })}
            />
          </div>
        ) : field.type === 'tel' ? (
          // A <div>, not a <label>: the picker's button would become the label's
          // target, so a tap on "Phone number" would open the country list.
          <div key={field.name} className="details-field">
            <span className="details-label" aria-hidden="true">
              {t(`flow.details.${field.name}`, { defaultValue: field.name })}
            </span>
            <PhoneInput
              name={field.name}
              value={values[field.name] ?? ''}
              onChange={(e164) => setValues((prev) => ({ ...prev, [field.name]: e164 }))}
              label={t(`flow.details.${field.name}`, { defaultValue: field.name })}
              placeholder={t(`flow.details.placeholder.${field.name}`, { defaultValue: '' })}
            />
          </div>
        ) : (
          <label key={field.name} className="details-field">
            <span className="details-label">
              {t(`flow.details.${field.name}`, { defaultValue: field.name })}
            </span>
            <input
              className="details-input"
              name={field.name}
              type={field.type}
              maxLength={field.maxLength}
              autoComplete={field.autoComplete}
              // An example of the expected FORMAT, never the label again.
              placeholder={t(`flow.details.placeholder.${field.name}`, { defaultValue: '' })}
              value={values[field.name] ?? ''}
              onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
            />
          </label>
        ),
      )}
      <div className="actions">
        <button type="submit" className="btn" disabled={!canSubmit} data-track="submit_details">
          {t('flow.event.DETAILS_SUBMITTED', { defaultValue: 'Continue' })}
        </button>
      </div>
    </form>
  );
}
